import React, { FC, useCallback, useMemo, useState } from 'react'
import { Button, Input, Space, Table } from 'antd'
import { useNavigate } from 'react-router-dom'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import type { FilterDropdownProps } from 'antd/es/table/interface'
import { SearchOutlined } from '@ant-design/icons'
import { getTableFilters, localeTableSorter, onFilterTableHandler, versionTableSorter } from '../../common/utils'
import './styles.css'
import { NodeInternalListItem } from '../../api/internal_types'

interface NodeTableData {
    nodeId: string
    farmId: string
    farmName: string
    battery: string
    firmwareVersion: string
    tags: string
}

type DeviceListItem = Pick<NodeInternalListItem, 'id' | 'firmwareVersion' | 'battery' | 'farm' | 'onomondo'>

interface Props {
    data?: DeviceListItem[]
    loading?: boolean
    itemLabel?: string
}

export const NodesList: FC<Props> = ({ data, loading, itemLabel = 'Node' }) => {
    const navigate = useNavigate()
    const [tableKey, setTableKey] = useState(0)

    const getTextSearchProps = useCallback(
        (
            dataIndex: keyof NodeTableData
        ): Pick<ColumnType<NodeTableData>, 'filterDropdown' | 'filterIcon' | 'onFilter'> => {
            return {
                filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
                    <div style={{ padding: 8 }}>
                        <Input
                            placeholder={`Search ${String(dataIndex)}`}
                            value={(selectedKeys[0] as string) ?? ''}
                            onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
                            onPressEnter={() => confirm()}
                            style={{ marginBottom: 8, display: 'block' }}
                        />
                        <Space>
                            <Button type="primary" onClick={() => confirm()} size="small">
                                Search
                            </Button>
                            <Button onClick={() => clearFilters?.()} size="small">
                                Reset
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered: boolean) => (
                    <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
                ),
                onFilter: (value: string | number | boolean, record: NodeTableData) => {
                    const recordValue = String(record[dataIndex] ?? '').toLowerCase()
                    return recordValue.includes(String(value).toLowerCase())
                },
            }
        },
        []
    )

    const tableData: NodeTableData[] = useMemo(() => {
        if (!data) {
            return []
        }

        return data.map(({ id, firmwareVersion = 'unknown', onomondo = { tags: [] }, battery, farm }) => {
            return {
                nodeId: id,
                farmId: farm?.id ?? '-',
                farmName: farm?.name ?? '-',
                battery: battery + '%',
                firmwareVersion: firmwareVersion,
                tags: onomondo.tags.join(','),
            }
        })
    }, [data])

    const columns: ColumnsType<NodeTableData> = useMemo(
        () => [
            {
                title: itemLabel,
                dataIndex: 'nodeId',
                ...getTextSearchProps('nodeId'),
                sorter: localeTableSorter<NodeTableData>('nodeId'),
            },
            {
                title: 'Farm ID',
                dataIndex: 'farmId',
                ...getTextSearchProps('farmId'),
                sorter: localeTableSorter<NodeTableData>('farmId'),
            },
            {
                title: 'Farm Name',
                dataIndex: 'farmName',
                ...getTextSearchProps('farmName'),
                sorter: localeTableSorter<NodeTableData>('farmName'),
            },
            {
                title: 'Battery',
                dataIndex: 'battery',
                // filterSearch: false,
                // sorter: localeTableSorter<NodeTableData>('battery'),
            },
            {
                title: 'Version',
                dataIndex: 'firmwareVersion',
                filterSearch: true,
                filters: getTableFilters<NodeTableData>(tableData, 'firmwareVersion'),
                onFilter: onFilterTableHandler<NodeTableData>('firmwareVersion'),
                sorter: versionTableSorter<NodeTableData>('firmwareVersion'),
            },
            {
                title: 'Tags',
                dataIndex: 'tags',
                filterSearch: true,
                filters: getTableFilters<NodeTableData>(tableData, 'tags'),
                onFilter: onFilterTableHandler<NodeTableData>('tags'),
                sorter: localeTableSorter<NodeTableData>('tags'),
            },
        ],
        [getTextSearchProps, tableData]
    )

    return (
        <div className="nodes-list__container">
            <div className="nodes-list__actions">
                <Button
                    className="nodes-list__reset-button"
                    size="small"
                    onClick={() => setTableKey((value) => value + 1)}
                >
                    Reset filters
                </Button>
            </div>
            <Table
                key={tableKey}
                loading={loading}
                pagination={{
                    pageSize: 20,
                }}
                columns={columns}
                dataSource={tableData}
                rowKey="nodeId"
                onRow={({ nodeId }) => {
                    return {
                        // onClick: () => nodeId && navigate(`/todo`),
                        // className: cultivationId ? '' : 'nodes-list__row--not-clickable'
                    }
                }}
                rowClassName="nodes-list__row"
            />
        </div>
    )
}
